import type { AxiosError, AxiosRequestConfig } from 'axios';

export interface RetryOptions {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  exponentialBackoff?: boolean;
  retryCondition?: (error: AxiosError) => boolean;
  onRetry?: (error: AxiosError, retryCount: number) => void;
  showNotifications?: boolean;
}

export interface RetryConfig {
  MAX_RETRIES: number;
  INITIAL_DELAY: number;
  MAX_DELAY: number;
}

// Default retry configuration
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  MAX_RETRIES: 3,
  INITIAL_DELAY: 1000,
  MAX_DELAY: 10000,
};

/**
 * Enhanced retry wrapper for API requests with intelligent retry logic
 */
export async function withRetry<T>(
  requestFn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = DEFAULT_RETRY_CONFIG.MAX_RETRIES,
    initialDelay = DEFAULT_RETRY_CONFIG.INITIAL_DELAY,
    maxDelay = DEFAULT_RETRY_CONFIG.MAX_DELAY,
    exponentialBackoff = true,
    retryCondition = defaultRetryCondition,
    onRetry,
    showNotifications = true,
  } = options;

  let lastError: AxiosError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error as AxiosError;

      // Don't retry if it's the final attempt
      if (attempt === maxRetries) {
        if (showNotifications && typeof window !== 'undefined') {
          // Show final error notification
          const message = extractFinalErrorMessage(lastError);
          console.error('Final retry failed:', message);
          // Note: We can't import useNotification here due to circular dependency
          // The consuming application should handle final error notifications
        }
        throw lastError;
      }

      // Check if we should retry this error
      if (!retryCondition(lastError)) {
        throw lastError;
      }

      // Calculate delay
      const delay = exponentialBackoff
        ? Math.min(initialDelay * Math.pow(2, attempt), maxDelay)
        : initialDelay;

      // Call onRetry callback if provided
      if (onRetry) {
        onRetry(lastError, attempt + 1);
      }

      // Show retry notification
      if (showNotifications && typeof window !== 'undefined') {
        const retryMessage = getRetryMessage(lastError, attempt + 1, maxRetries, delay);
        console.log('Retrying request:', retryMessage);
        // Note: We can't import useNotification here due to circular dependency
        // The consuming application should handle retry notifications
      }

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError!;
}

/**
 * Default retry condition - retry on network errors and server errors
 */
function defaultRetryCondition(error: AxiosError): boolean {
  // Retry on network errors
  if (isNetworkError(error)) {
    return true;
  }

  // Retry on server errors (5xx)
  if (error.response && error.response.status >= 500) {
    return true;
  }

  // Retry on rate limiting (429) with exponential backoff
  if (error.response?.status === 429) {
    return true;
  }

  // Don't retry on authentication errors
  if (error.response?.status === 401) {
    return false;
  }

  // Don't retry on client errors (4xx) except 429
  if (error.response && error.response.status >= 400 && error.response.status < 500) {
    return false;
  }

  return false;
}

/**
 * Check if error is a network error
 */
function isNetworkError(error: AxiosError): boolean {
  return !error.response;
}

/**
 * Generate user-friendly retry message
 */
function getRetryMessage(
  error: AxiosError,
  retryCount: number,
  maxRetries: number,
  delay: number
): string {
  const timeRemaining = (delay / 1000).toFixed(1);

  if (isNetworkError(error)) {
    return `Connection failed. Retrying in ${timeRemaining}s... (${retryCount}/${maxRetries})`;
  }

  if (error.response?.status === 429) {
    return `Rate limit exceeded. Waiting ${timeRemaining}s before retry... (${retryCount}/${maxRetries})`;
  }

  if (error.response && error.response.status >= 500) {
    return `Server error. Retrying in ${timeRemaining}s... (${retryCount}/${maxRetries})`;
  }

  return `Request failed. Retrying in ${timeRemaining}s... (${retryCount}/${maxRetries})`;
}

/**
 * Extract final error message when all retries are exhausted
 */
function extractFinalErrorMessage(error: AxiosError): string {
  if (isNetworkError(error)) {
    return 'Unable to connect to the server after multiple attempts. Please check your internet connection.';
  }

  if (error.response?.status === 429) {
    return 'Service is currently experiencing high demand. Please try again later.';
  }

  if (error.response && error.response.status >= 500) {
    return 'Server is experiencing issues. Please try again later or contact support if the problem persists.';
  }

  return 'Request failed after multiple attempts. Please try again later.';
}

/**
 * Specialized retry for authentication requests
 */
export async function withAuthRetry<T>(
  requestFn: () => Promise<T>,
  options: Omit<RetryOptions, 'retryCondition'> = {}
): Promise<T> {
  return withRetry(requestFn, {
    ...options,
    maxRetries: 2, // Fewer retries for auth requests
    retryCondition: (error: AxiosError) => {
      // Only retry on network errors for auth requests
      return isNetworkError(error);
    },
  });
}

/**
 * Specialized retry for file operations
 */
export async function withFileRetry<T>(
  requestFn: () => Promise<T>,
  options: Omit<RetryOptions, 'retryCondition'> = {}
): Promise<T> {
  return withRetry(requestFn, {
    ...options,
    maxRetries: 5, // More retries for file operations
    initialDelay: 2000, // Longer initial delay
    retryCondition: (error: AxiosError) => {
      // Retry on network errors and server errors for file operations
      return (
        isNetworkError(error) ||
        (error.response && error.response.status >= 500) ||
        error.response?.status === 429
      );
    },
  });
}

/**
 * Create a retry wrapper for critical operations
 */
export async function withCriticalRetry<T>(
  requestFn: () => Promise<T>,
  options: Omit<RetryOptions, 'maxRetries'> = {}
): Promise<T> {
  return withRetry(requestFn, {
    ...options,
    maxRetries: 5, // More retries for critical operations
    retryCondition: (error: AxiosError) => {
      // Be more aggressive with retries for critical operations
      return (
        isNetworkError(error) ||
        (error.response && error.response.status >= 500) ||
        error.response?.status === 429 ||
        error.response?.status === 408
      ); // Request timeout
    },
  });
}

/**
 * Request cancellation utility
 */
export class RequestCancellation {
  private abortController: AbortController;

  constructor() {
    this.abortController = new AbortController();
  }

  get signal() {
    return this.abortController.signal;
  }

  cancel(reason?: string) {
    this.abortController.abort(reason);
    // Create a new controller for future requests
    this.abortController = new AbortController();
  }

  createConfig(config: AxiosRequestConfig = {}): AxiosRequestConfig {
    return {
      ...config,
      signal: this.abortController.signal,
    };
  }
}

/**
 * Create a cancellable request with retry logic
 */
export function withCancellation<T>(requestFn: (signal: AbortSignal) => Promise<T>): {
  request: () => Promise<T>;
  cancel: (reason?: string) => void;
} {
  const cancellation = new RequestCancellation();

  const request = () => requestFn(cancellation.signal);
  const cancel = (reason?: string) => cancellation.cancel(reason);

  return { request, cancel };
}

/**
 * Exponential backoff delay calculator
 */
export function calculateBackoffDelay(
  attempt: number,
  initialDelay: number = 1000,
  maxDelay: number = 30000,
  multiplier: number = 2
): number {
  const delay = initialDelay * Math.pow(multiplier, attempt);
  return Math.min(delay, maxDelay);
}

/**
 * Jittered delay to avoid thundering herd
 */
export function addJitter(delay: number, jitterFactor: number = 0.1): number {
  const jitter = delay * jitterFactor * Math.random();
  return delay + jitter;
}

/**
 * Circuit breaker pattern for failing services
 */
export class CircuitBreaker {
  private failures = 0;
  private lastFailureTime = 0;
  private state: 'closed' | 'open' | 'half-open' = 'closed';

  constructor(
    private failureThreshold: number = 5,
    private timeout: number = 60000, // 1 minute
    private monitoringPeriod: number = 10000 // 10 seconds
  ) {}

  async execute<T>(fn: () => Promise<T>): Promise<T> {
    if (this.state === 'open') {
      if (Date.now() - this.lastFailureTime > this.timeout) {
        this.state = 'half-open';
      } else {
        throw new Error('Circuit breaker is open');
      }
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  private onSuccess() {
    this.failures = 0;
    this.state = 'closed';
  }

  private onFailure() {
    this.failures++;
    this.lastFailureTime = Date.now();

    if (this.failures >= this.failureThreshold) {
      this.state = 'open';
    }
  }

  getState() {
    return this.state;
  }

  reset() {
    this.failures = 0;
    this.state = 'closed';
    this.lastFailureTime = 0;
  }
}

/**
 * Rate limiter to prevent too many requests
 */
export class RateLimiter {
  private requests: number[] = [];

  constructor(
    private maxRequests: number = 100,
    private windowMs: number = 60000 // 1 minute
  ) {}

  canMakeRequest(): boolean {
    const now = Date.now();

    // Remove old requests outside the window
    this.requests = this.requests.filter(time => now - time < this.windowMs);

    return this.requests.length < this.maxRequests;
  }

  makeRequest(): boolean {
    if (this.canMakeRequest()) {
      this.requests.push(Date.now());
      return true;
    }
    return false;
  }

  getRemainingRequests(): number {
    const now = Date.now();
    this.requests = this.requests.filter(time => now - time < this.windowMs);
    return Math.max(0, this.maxRequests - this.requests.length);
  }

  getResetTime(): number {
    if (this.requests.length === 0) return 0;
    return this.requests[0] + this.windowMs;
  }
}
